import { useState, useEffect } from "react";
import axios from "axios";
import { Newspaper, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Research is the default view. The trade feeds publish many times a day and
// the journals a few times a week, so anything that shows them together ends
// up mostly trade — and the papers are the reason this widget exists.
const DEFAULT_FILTER = "research";

const CATEGORY_TONE = {
  research: "bg-sky-50 text-sky-700 border-sky-100",
  industry: "bg-amber-50 text-amber-700 border-amber-100",
};

// Items are kept for forty-five days, so the date form below is reached for
// anything in the last two weeks of that window. It follows the language
// chosen in Core: passing undefined hands the decision to the operating
// system, which is how an Italian page ends up writing "Aug 27".
function timeAgo(iso, s, language) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then)) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days < 1) return s("feed.justNow");
  if (days === 1) return s("feed.yesterday");
  if (days < 30) return `${days}d`;
  return then.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
    day: "numeric",
    month: "short",
  });
}

/**
 * Recent publications and industry news in the radiopharmaceutical field.
 *
 * Titles only, each linking straight to the publisher. Laid out in columns:
 * academic titles run long, and a single column of them turns into a wall.
 */
export default function NewsWidget() {
  const { language } = useLanguage();
  const s = localStrings(language);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState(DEFAULT_FILTER);

  // Fetched per filter rather than once and sliced on this side. The server
  // decides what "recent" means for each kind, and asking it directly is the
  // only way a tab can show six items when the other kind has fifty newer
  // ones.
  useEffect(() => {
    let cancelled = false;
    const params = { limit: 12 };
    if (filter !== "all") params.category = filter;

    axios
      .get(`${API}/news`, { params, withCredentials: true })
      .then((res) => { if (!cancelled) setItems(res.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });

    return () => { cancelled = true; };
  }, [filter]);

  // Nothing at all on the default view: render nothing rather than an empty
  // frame. A decoration with nothing on it should not draw attention to the
  // gap.
  if (items === null || (items.length === 0 && filter === DEFAULT_FILTER)) return null;

  const tabs = [
    { id: "research", label: s("news.research") },
    { id: "industry", label: s("news.industry") },
    { id: "all", label: s("news.all") },
  ];

  return (
    <section data-testid="news-widget">
      {/* Sky rather than the grey every other label wears: it is the tone the
          Research badge already carries below, and Research is the tab this
          opens on, so the heading matches what is under it on arrival. */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center gap-1.5">
          <Newspaper className="w-3.5 h-3.5 text-sky-600" />
          <span className="text-xs font-semibold uppercase tracking-widest text-sky-600">
            {s("news.title")}
          </span>
        </div>

        <div className="flex gap-1 ml-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              data-testid={`news-filter-${tab.id}`}
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full transition-colors ${
                filter === tab.id
                  ? "bg-slate-800 text-white"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">{s("news.emptyFilter")}</p>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 divide-y md:divide-y-0 divide-slate-100">
            {items.slice(0, 6).map((item) => (
              <a
                key={item.id}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-item-${item.id}`}
                className="group flex flex-col gap-2 p-4 hover:bg-slate-50 transition-colors md:border-r md:border-slate-100 md:last:border-r-0"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                      CATEGORY_TONE[item.category] || "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                  >
                    {item.source_label}
                  </span>
                  {/* Where a source aggregates others, the actual publisher is
                      the more useful name of the two. */}
                  {item.publisher && item.publisher !== item.source_label && (
                    <span className="text-[11px] text-slate-500 truncate max-w-[10rem]">
                      {item.publisher}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-400 ml-auto">
                    {timeAgo(item.published_at, s, language)}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 shrink-0 transition-colors" />
                </div>

                {/* Clamped to four lines: these titles routinely run past thirty
                    words, and letting one of them set the height of its column
                    would leave the others padded out with white space. */}
                <p className="text-sm text-slate-700 leading-snug line-clamp-4 group-hover:text-indigo-800 transition-colors">
                  {item.title}
                </p>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
